// The series behind a person's numbers, across days, weeks or months.
//
// A table, not a chart, and that is a decision rather than laziness. Most of
// these series are three to six points long today, several are counts, and the
// question a person asks of them is "what was it in July" rather than "what
// shape is it". A chart over four points implies a trend that four points cannot
// support. Tabular figures with tabular-nums answer the real question exactly,
// and a chart can be added when a series is long enough to have a shape.
//
// A missing period renders as words. A part period is labelled. Neither is ever
// drawn as a zero, which on a chart is exactly what a gap would look like.
import { formatPeriod, formatValue, NOT_MEASURED, slotLabel, type Grain, type SeriesRow } from '../../utils/operator'
import { Spinner } from '../../lib/shadcn/spinner'

type Group = {
  metric_id: string
  name: string
  slot: SeriesRow['slot']
  unit: string | null
  measure_label: string
  effective_target: number | null
  points: SeriesRow[]
}

function group(rows: SeriesRow[]): Group[] {
  const map = new Map<string, Group>()
  for (const row of rows) {
    const existing = map.get(row.metric_id)
    if (existing) {
      existing.points.push(row)
    } else {
      map.set(row.metric_id, {
        metric_id: row.metric_id,
        name: row.name,
        slot: row.slot,
        unit: row.unit,
        measure_label: row.measure_label,
        effective_target: row.effective_target,
        points: [row],
      })
    }
  }
  for (const g of map.values()) {
    g.points.sort((a, b) => a.period_start.localeCompare(b.period_start))
  }
  return [...map.values()]
}

export function NumberSeries({
  rows,
  grain,
  loading,
}: {
  rows: SeriesRow[]
  grain: Grain
  loading: boolean
}) {
  const groups = group(rows)

  if (groups.length === 0) {
    return (
      <div className="flex items-center gap-2 border-t border-border px-4 py-3 text-xs text-muted-foreground">
        {loading ? <Spinner className="h-3.5 w-3.5" /> : null}
        {loading
          ? 'Loading the figures.'
          : `Nothing measured at ${grain} rhythm yet. A number appears here once it is bound to a measure and the figures have been recomputed.`}
      </div>
    )
  }

  // Every period any of this person's numbers has a row for, so the columns line
  // up across numbers and a gap in one is visible against the others.
  const periods = [...new Set(rows.map((r) => r.period_start))].sort()

  return (
    <div className="border-t border-border">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Number
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Target
              </th>
              {periods.map((p) => (
                <th
                  key={p}
                  className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground"
                >
                  {formatPeriod(p, grain)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.metric_id} className="border-b border-border last:border-0">
                <td className="px-4 py-2 align-top">
                  <div className="font-medium text-foreground">{g.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {slotLabel(g.slot)} · measured as {g.measure_label}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {g.effective_target === null
                    ? 'none'
                    : formatValue(g.effective_target, g.unit)}
                </td>
                {periods.map((p) => {
                  const point = g.points.find((x) => x.period_start === p)
                  if (!point || point.value === null) {
                    return (
                      <td
                        key={p}
                        className="whitespace-nowrap px-3 py-2 text-right text-xs text-muted-foreground"
                      >
                        {NOT_MEASURED}
                      </td>
                    )
                  }
                  const cls =
                    point.on_target === null
                      ? 'text-foreground'
                      : point.on_target
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-red-700 dark:text-red-400'
                  return (
                    <td
                      key={p}
                      className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${cls}`}
                    >
                      {formatValue(point.value, g.unit)}
                      {point.is_partial_period ? (
                        <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-400">
                          part
                        </span>
                      ) : null}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-2 text-xs text-muted-foreground">
        Green and red are against the target on that number, and only where both the figure and the
        target are known. A period marked &quot;part&quot; has not finished, so it is not comparable
        with a whole one.
      </p>
    </div>
  )
}
